import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import LogRocket from 'logrocket';
import SubmitButton from 'base/components/forms/SubmitButton';
import FormikInputField from 'base/components/forms/formik/FormikInputField';
import MainTitle from 'base/components/MainTitle';
import SmallLabel from 'base/components/SmallLabel';
import theme, { textBold } from 'theme/theme';
import SpinnerOverlay from 'base/components/SpinnerOverlay/index';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import FormikCheckboxField from 'base/components/forms/formik/FormikCheckboxField';
import NavLink from 'base/components/NavLink';
import SplashScreen from 'auth/components/SplashScreen';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMatch } from 'react-router';
import { getIsAuthenticating, getHasError, getRequires2Fa, getIs2FaSetupRequired } from '../../store/auth/selectors';
import { login, sso } from '../../store/auth/actions';
import Setup2Fa from '../Setup2Fa/Setup2Fa';

const ErrorMessage = styled.div`
  margin: 0 0 2rem 0;
  ${textBold};
  color: ${theme.colors.primary};
`;

const SmallLink = styled(NavLink)`
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.medium};
  &:hover {
    color: ${theme.colors.primary};
    cursor: pointer;
  }
`;

export default function Login() {
  const dispatch = useAppDispatch();
  const isAuthenticating = useAppSelector(getIsAuthenticating);
  const hasError = useAppSelector(getHasError);
  const requires2Fa = useAppSelector(getRequires2Fa);
  const is2FaSetupRequired = useAppSelector(getIs2FaSetupRequired);
  const hasFinished2FaSetup = useMatch('/login/2fa-setup-success');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState(false);

  useEffect(() => {
    dispatch(sso());
  }, [dispatch]);

  const resetAuth = useCallback(() => {
    setEmail('');
    setPassword('');
    setPrivacyPolicy(false);
  }, []);

  const handleSubmit = useCallback(
    values => {
      setEmail(values.email);
      setPassword(values.password);
      setPrivacyPolicy(values.privacyPolicy);
      LogRocket.identify(values.email, {
        name: values.email,
        email: values.email,
      });
      dispatch(login(values.email, values.password, ''));
    },
    [dispatch],
  );

  const userNamePasswordLoginForm = (
    <>
      {hasError && <ErrorMessage>Could not log in. Please try again.</ErrorMessage>}
      {hasFinished2FaSetup && !hasError && (
        <FieldWrapper>
          <ErrorMessage>
            You have set up 2-factor authentication successfully!
            <br />
            For security reasons, you are required to log in again.
          </ErrorMessage>
        </FieldWrapper>
      )}
      {isAuthenticating ? (
        <SpinnerOverlay label="Authenticating" />
      ) : (
        <Formik
          initialValues={{
            email: hasError && !requires2Fa ? '' : email,
            password: hasError && !requires2Fa ? '' : password,
            privacyPolicy: hasError && !requires2Fa ? false : privacyPolicy,
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string().required('Email is required.').email('Please enter a valid email'),
            password: Yup.string().required('Password is required.'),
            privacyPolicy: Yup.mixed().oneOf([true], 'Please acceppt the privacy policy.'),
          })}
          render={({ isSubmitting }) => (
            // eslint-disable-line
            <Form>
              <Field type="text" name="email" placeholder="Email" autoFocus component={FormikInputField} />
              <Field type="password" name="password" placeholder="Password" component={FormikInputField} />
              <Field name="privacyPolicy" component={FormikCheckboxField}>
                <SmallLabel>
                  I accept the <NavLink to="/privacy-policy">privacy policy</NavLink>
                </SmallLabel>
              </Field>

              <SubmitButton isFullWidth type="submit" disabled={isSubmitting}>
                Login
              </SubmitButton>

              <SmallLink to="/forgot-password" style={{ paddingTop: '2rem', display: 'block' }}>
                Forgot password
              </SmallLink>
            </Form>
          )}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );

  const otpForm = (
    <>
      {hasError && <ErrorMessage>Invalid authentication code. Please try again.</ErrorMessage>}
      {isAuthenticating ? (
        <SpinnerOverlay label="Authenticating" />
      ) : (
        <Formik
          initialValues={{ otp: '' }}
          validationSchema={Yup.object().shape({
            otp: Yup.string().required('Authentication code is required.'),
          })}
          render={({ isSubmitting }) => (
            // eslint-disable-line
            <Form>
              <>
                <Field type="text" name="otp" label="Authentication code" autoFocusasdf component={FormikInputField} />
                <SmallLabel style={{ maxWidth: '300px', paddingBottom: '2rem' }}>
                  Open the two-factor authentication app on your device to view your authentication code and verify your
                  identity.
                </SmallLabel>
              </>

              <SubmitButton isFullWidth type="submit" disabled={isSubmitting}>
                Login
              </SubmitButton>
            </Form>
          )}
          onSubmit={values => {
            dispatch(login(email, password, values.otp));
          }}
        />
      )}
    </>
  );

  const loginForm = (
    <>
      <MainTitle withSpaceBelow>Login</MainTitle>
      {requires2Fa ? otpForm : userNamePasswordLoginForm}
    </>
  );

  const setup2FaForm = (
    <>
      <MainTitle>2-factor authentication setup required</MainTitle>
      <FieldWrapper>
        You are required to set up 2-factor authentication in order to access the Bucher Corporate Intranet.
      </FieldWrapper>
      <Setup2Fa onSubmit={resetAuth} />
    </>
  );

  return <SplashScreen>{is2FaSetupRequired ? setup2FaForm : loginForm}</SplashScreen>;
}
